import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, Search, Edit, Trash2, X, Printer, ArrowRightCircle } from 'lucide-react';
import { QuickCreateCustomer, QuickCreateProduct } from '../components/QuickCreateModals';
import { toast } from 'sonner';

const NOTES_MAX = 1000;
const emptyAddress = { address_line1: '', address_line2: '', city: '', state: '', pincode: '', country: 'India' };

const Quotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);
  const [showQuickProduct, setShowQuickProduct] = useState(false);

  const emptyForm = {
    customer_id: '', billing_address: null, shipping_address: null,
    quotation_date: '', valid_until: '', notes: '',
    items: [{ sku: '', name: '', item_code: '', hsn_code: '', quantity: 0, unit: 'Nos', qty_per_kg: '', qty_per_pouch: '', moq: '', unit_price: 0, gst_rate: 18 }]
  };
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [qRes, cRes, iRes] = await Promise.all([
        api.get('/sales/quotations'),
        api.get('/purchase/customers'),
        api.get('/sales/inventory')
      ]);
      setQuotations(qRes.data); setCustomers(cRes.data); setInventory(iRes.data);
    } catch (error) { console.error('Failed to fetch data:', error); }
    finally { setLoading(false); }
  };

  const selectCustomer = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customer_id: customerId,
        billing_address: { ...emptyAddress, ...(customer.billing_address || {}) },
        shipping_address: { ...emptyAddress, ...(customer.shipping_address || customer.billing_address || {}) }
      }));
    } else {
      setFormData(prev => ({ ...prev, customer_id: customerId }));
    }
  };

  const updateAddress = (type, field, value) => setFormData(prev => ({ ...prev, [type]: { ...(prev[type] || emptyAddress), [field]: value } }));
  const copyBillingToShipping = () => setFormData(prev => ({ ...prev, shipping_address: { ...(prev.billing_address || emptyAddress) } }));

  const calculateItemTotals = (item) => {
    const taxable = item.quantity * item.unit_price;
    const gstAmount = (taxable * item.gst_rate) / 100;
    return { ...item, taxable_amount: taxable, cgst: gstAmount / 2, sgst: gstAmount / 2, igst: 0, total_amount: taxable + gstAmount };
  };

  const calcTotals = () => {
    let subtotal = 0, cgstTotal = 0, sgstTotal = 0;
    formData.items.forEach(item => { const c = calculateItemTotals(item); subtotal += c.taxable_amount; cgstTotal += c.cgst; sgstTotal += c.sgst; });
    return { subtotal, cgstTotal, sgstTotal, total: subtotal + cgstTotal + sgstTotal };
  };
  const totals = calcTotals();

  const addItem = () => setFormData(prev => ({ ...prev, items: [...prev.items, { sku: '', name: '', item_code: '', hsn_code: '', quantity: 0, unit: 'Nos', qty_per_kg: '', qty_per_pouch: '', moq: '', unit_price: 0, gst_rate: 18 }] }));
  const removeItem = (index) => { if (formData.items.length <= 1) return; setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) })); };
  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'sku' && value) {
      const inv = inventory.find(i => i.sku === value);
      if (inv) {
        newItems[index].name = inv.name;
        newItems[index].item_code = inv.sku || '';
        newItems[index].hsn_code = inv.hsn_code || '';
        newItems[index].unit = inv.unit || 'Nos';
        newItems[index].unit_price = inv.unit_price || 0;
        newItems[index].gst_rate = inv.gst_rate || 18;
      }
    }
    setFormData({ ...formData, items: newItems });
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm }); setShowModal(true); };

  const openEdit = (quo) => {
    setEditing(quo);
    setFormData({
      customer_id: quo.customer_id,
      billing_address: { ...emptyAddress, ...(quo.billing_address || {}) },
      shipping_address: { ...emptyAddress, ...(quo.shipping_address || {}) },
      quotation_date: '', valid_until: quo.valid_until || '',
      notes: quo.notes || '',
      items: (quo.items || []).map(i => ({ ...i }))
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const itemsWithTotals = formData.items.map(calculateItemTotals);
      if (editing) {
        await api.put(`/sales/quotations/${editing.id}`, { ...formData, items: itemsWithTotals });
      } else {
        await api.post('/sales/quotations', { ...formData, items: itemsWithTotals });
      }
      setShowModal(false); setEditing(null); setFormData({ ...emptyForm }); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save quotation'); }
  };

  const setStatus = async (quo, status) => {
    try { await api.put(`/sales/quotations/${quo.id}`, { status }); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to update status'); }
  };

  const convertToOrder = async (quo) => {
    if (!window.confirm(`Convert quotation "${quo.quotation_number}" to a Sales Order?`)) return;
    try {
      const res = await api.post(`/sales/quotations/${quo.id}/convert`);
      toast.success(`Sales Order ${res.data.order_number} created`);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to convert'); }
  };

  const deleteQuotation = async (quo) => {
    if (!window.confirm(`Delete quotation "${quo.quotation_number}"?`)) return;
    try { await api.delete(`/sales/quotations/${quo.id}`); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
  };

  const printQuotation = (quo) => {
    const w = window.open('', '_blank');
    const items = quo.items || [];
    w.document.write(`
      <html><head><title>Quotation - ${quo.quotation_number}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:32px;font-size:13px;color:#222}
        h1{margin:0;font-size:22px}h2{margin:0;font-size:14px;font-weight:400;color:#666}
        .header{display:flex;justify-content:space-between;border-bottom:2px solid #000;padding-bottom:16px;margin-bottom:16px}
        .section-title{font-weight:700;font-size:12px;text-transform:uppercase;color:#666;margin-bottom:4px}
        table{width:100%;border-collapse:collapse;margin:16px 0}
        th,td{border:1px solid #ccc;padding:8px 10px;text-align:left;font-size:12px}
        th{background:#f0f0f0;font-weight:600}td.num{text-align:right}
        .totals{float:right;width:280px;margin-top:16px}
        .totals .row{display:flex;justify-content:space-between;padding:4px 0}
        .totals .row.total{border-top:2px solid #000;font-weight:700;font-size:15px;padding-top:8px}
        @media print{button{display:none}}
      </style></head><body>
      <div class="header">
        <div><h1>Quotation</h1><h2>${quo.quotation_number}</h2></div>
        <div style="text-align:right"><div style="font-size:14px;font-weight:700">${quo.customer_name}</div>
        <div>${quo.customer_gstin || 'No GSTIN'}</div>
        <div>Date: ${quo.quotation_date || new Date(quo.created_at).toLocaleDateString()}</div>
        ${quo.valid_until ? `<div>Valid Until: ${quo.valid_until}</div>` : ''}
        <div>Status: ${(quo.status || '').toUpperCase()}</div></div>
      </div>
      ${quo.billing_address ? `<div class="section-title">Billing Address</div>
        <div>${quo.billing_address.address_line1 || ''}${quo.billing_address.city ? ', ' + quo.billing_address.city : ''} ${quo.billing_address.state || ''} ${quo.billing_address.pincode || ''}</div>` : ''}
      <table><thead><tr><th>#</th><th>SKU</th><th>Name</th><th>Qty</th><th>Unit</th><th>Qty/Kg</th><th>Qty/Pouch</th><th>MOQ</th><th>Rate</th></tr></thead><tbody>
      ${items.map((i, idx) => `<tr><td>${idx + 1}</td><td>${i.sku}</td><td>${i.name}</td>
      <td class="num">${i.quantity}</td><td>${i.unit || '-'}</td><td class="num">${i.qty_per_kg ?? '-'}</td><td class="num">${i.qty_per_pouch ?? '-'}</td><td class="num">${i.moq ?? '-'}</td><td class="num">${(i.unit_price || 0).toFixed(2)}</td></tr>`).join('')}
      </tbody></table>
      <div style="clear:both"></div>
      ${quo.notes ? `<div style="margin-top:24px"><div class="section-title">Notes</div><div>${quo.notes}</div></div>` : ''}
      <button onclick="window.print()" style="margin-top:24px;padding:8px 24px;cursor:pointer">Print</button>
      </body></html>`);
    w.document.close();
  };

  const getStatusBadge = (status) => {
    const badges = { draft: 'badge-warning', sent: 'badge-info', accepted: 'badge-success', rejected: 'badge-danger', converted: 'badge-success', expired: 'badge-danger' };
    return `badge ${badges[status] || 'badge-info'}`;
  };

  const filtered = quotations.filter(q => {
    const s = searchTerm.toLowerCase();
    const matchSearch = !s || (q.quotation_number || '').toLowerCase().includes(s) || (q.customer_name || '').toLowerCase().includes(s);
    const matchStatus = statusFilter === 'all' || q.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div data-testid="quotations-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Quotations</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="create-quotation-btn"><Plus size={16} /> Create Quotation</button>
      </div>

      <div className="card mb-4">
        <div className="grid grid-cols-2" style={{ gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
            <input type="text" className="form-input" placeholder="Search by quotation # or customer..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
          </div>
          <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="converted">Converted</option>
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? <div data-testid="loading-indicator">Loading quotations...</div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr><th>Quotation #</th><th>Customer</th><th>Items</th><th className="text-right">Amount</th><th>Valid Until</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No quotations</td></tr> : filtered.map((quo) => (
                  <tr key={quo.id} data-testid={`quotation-row-${quo.quotation_number}`}>
                    <td className="font-mono">{quo.quotation_number}</td>
                    <td><div>{quo.customer_name}</div><div className="text-xs text-muted">{quo.customer_gstin || 'No GSTIN'}</div></td>
                    <td>{quo.items?.length || 0} items</td>
                    <td className="text-right tabular-nums">{(quo.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, style: 'currency', currency: 'INR' })}</td>
                    <td className="text-sm">{quo.valid_until || '-'}</td>
                    <td>
                      {quo.status === 'converted'
                        ? <span className={getStatusBadge(quo.status)} title={`Order ${quo.converted_order_number || ''}`}>converted</span>
                        : <select value={quo.status} onChange={(e) => setStatus(quo, e.target.value)} className={getStatusBadge(quo.status)} style={{ border: 'none', cursor: 'pointer', fontSize: '11px' }} data-testid={`status-${quo.quotation_number}`}>
                            <option value="draft">draft</option>
                            <option value="sent">sent</option>
                            <option value="accepted">accepted</option>
                            <option value="rejected">rejected</option>
                          </select>}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => printQuotation(quo)} className="btn btn-ghost btn-sm" title="Print" data-testid={`print-btn-${quo.quotation_number}`}><Printer size={14} /></button>
                        {quo.status !== 'converted' && (
                          <button onClick={() => convertToOrder(quo)} className="btn btn-ghost btn-sm" title="Convert to Sales Order" data-testid={`convert-btn-${quo.quotation_number}`}><ArrowRightCircle size={14} color="#059669" /></button>
                        )}
                        {quo.status !== 'converted' && (
                          <button onClick={() => openEdit(quo)} className="btn btn-ghost btn-sm" title="Edit" data-testid={`edit-btn-${quo.quotation_number}`}><Edit size={14} /></button>
                        )}
                        {quo.status !== 'converted' && (
                          <button onClick={() => deleteQuotation(quo)} className="btn btn-ghost btn-sm" title="Delete" data-testid={`delete-btn-${quo.quotation_number}`}><Trash2 size={14} color="#DC2626" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="quotation-modal">
          <div className="modal-content" style={{ maxWidth: '1000px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? `Edit Quotation ${editing.quotation_number}` : 'Create Quotation'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2" style={{ gap: '16px' }}>
                  <div className="form-group">
                    <div className="flex items-center justify-between" style={{ marginBottom: '4px' }}>
                      <label className="form-label" style={{ margin: 0 }}>Customer *</label>
                      <button type="button" onClick={() => setShowQuickCustomer(true)} className="btn btn-ghost btn-sm" style={{ fontSize: '11px', padding: '2px 8px' }}><Plus size={12} /> New</button>
                    </div>
                    <select className="form-select" value={formData.customer_id} onChange={(e) => selectCustomer(e.target.value)} required disabled={!!editing} data-testid="customer-select">
                      <option value="">Select customer...</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.name} - {c.customer_code}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Valid Until</label>
                    <input type="date" className="form-input" value={formData.valid_until} onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })} data-testid="valid-until-input" />
                  </div>
                </div>

                {formData.billing_address && (
                  <div className="grid grid-cols-2" style={{ marginBottom: '16px', gap: '16px' }}>
                    <div style={{ padding: '12px', backgroundColor: 'var(--bg-surface-alt)', borderRadius: '4px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}>Billing Address</div>
                      <input type="text" className="form-input" style={{ marginBottom: '6px', fontSize: '13px' }} placeholder="Address line 1" value={formData.billing_address.address_line1 || ''} onChange={(e) => updateAddress('billing_address', 'address_line1', e.target.value)} />
                      <input type="text" className="form-input" style={{ marginBottom: '6px', fontSize: '13px' }} placeholder="Address line 2" value={formData.billing_address.address_line2 || ''} onChange={(e) => updateAddress('billing_address', 'address_line2', e.target.value)} />
                      <div className="grid grid-cols-3" style={{ gap: '6px' }}>
                        <input type="text" className="form-input" style={{ fontSize: '13px' }} placeholder="City" value={formData.billing_address.city || ''} onChange={(e) => updateAddress('billing_address', 'city', e.target.value)} />
                        <input type="text" className="form-input" style={{ fontSize: '13px' }} placeholder="State" value={formData.billing_address.state || ''} onChange={(e) => updateAddress('billing_address', 'state', e.target.value)} />
                        <input type="text" className="form-input" style={{ fontSize: '13px' }} placeholder="Pincode" value={formData.billing_address.pincode || ''} onChange={(e) => updateAddress('billing_address', 'pincode', e.target.value)} />
                      </div>
                    </div>
                    <div style={{ padding: '12px', backgroundColor: 'var(--bg-surface-alt)', borderRadius: '4px' }}>
                      <div className="flex items-center justify-between" style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600 }}>Shipping Address</div>
                        <button type="button" onClick={copyBillingToShipping} className="btn btn-ghost btn-sm" style={{ fontSize: '11px', padding: '2px 6px' }}>Same as billing</button>
                      </div>
                      <input type="text" className="form-input" style={{ marginBottom: '6px', fontSize: '13px' }} placeholder="Address line 1" value={formData.shipping_address?.address_line1 || ''} onChange={(e) => updateAddress('shipping_address', 'address_line1', e.target.value)} />
                      <input type="text" className="form-input" style={{ marginBottom: '6px', fontSize: '13px' }} placeholder="Address line 2" value={formData.shipping_address?.address_line2 || ''} onChange={(e) => updateAddress('shipping_address', 'address_line2', e.target.value)} />
                      <div className="grid grid-cols-3" style={{ gap: '6px' }}>
                        <input type="text" className="form-input" style={{ fontSize: '13px' }} placeholder="City" value={formData.shipping_address?.city || ''} onChange={(e) => updateAddress('shipping_address', 'city', e.target.value)} />
                        <input type="text" className="form-input" style={{ fontSize: '13px' }} placeholder="State" value={formData.shipping_address?.state || ''} onChange={(e) => updateAddress('shipping_address', 'state', e.target.value)} />
                        <input type="text" className="form-input" style={{ fontSize: '13px' }} placeholder="Pincode" value={formData.shipping_address?.pincode || ''} onChange={(e) => updateAddress('shipping_address', 'pincode', e.target.value)} />
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Quotation Items</label>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '4px' }}>SKU</th><th style={{ padding: '4px' }}>Name</th>
                          <th style={{ padding: '4px' }}>Item Code</th><th style={{ padding: '4px' }}>HSN</th>
                          <th style={{ padding: '4px' }}>Qty</th><th style={{ padding: '4px' }}>Unit</th>
                          <th style={{ padding: '4px' }}>Qty/Kg</th><th style={{ padding: '4px' }}>Qty/Pouch</th><th style={{ padding: '4px' }}>MOQ</th>
                          <th style={{ padding: '4px' }}>Price</th><th style={{ padding: '4px' }}>GST%</th>
                          <th style={{ padding: '4px', textAlign: 'right' }}>Amount</th><th style={{ padding: '4px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.items.map((item, index) => {
                          const itemTotal = calculateItemTotals(item);
                          return (
                            <tr key={index}>
                              <td style={{ padding: '4px' }}>
                                <select className="form-select" value={item.sku} onChange={(e) => updateItem(index, 'sku', e.target.value)} required style={{ minWidth: '100px', fontSize: '12px', padding: '4px' }} data-testid={`item-sku-${index}`}>
                                  <option value="">Select...</option>
                                  {inventory.map((inv) => <option key={inv.sku} value={inv.sku}>{inv.sku}</option>)}
                                </select>
                              </td>
                              <td style={{ padding: '4px' }}><input type="text" className="form-input" value={item.name} readOnly style={{ minWidth: '120px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px' }}><input type="text" className="form-input" value={item.item_code || ''} onChange={(e) => updateItem(index, 'item_code', e.target.value)} style={{ width: '90px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px' }}><input type="text" className="form-input" value={item.hsn_code} readOnly style={{ width: '80px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px' }}><input type="number" className="form-input" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 0)} required style={{ width: '60px', fontSize: '12px', padding: '4px' }} data-testid={`item-qty-${index}`} /></td>
                              <td style={{ padding: '4px' }}>
                                <select className="form-select" value={item.unit || 'Nos'} onChange={(e) => updateItem(index, 'unit', e.target.value)} style={{ width: '70px', fontSize: '12px', padding: '4px' }}>
                                  <option value="Nos">No's</option><option value="Kgs">Kg's</option>
                                </select>
                              </td>
                              <td style={{ padding: '4px' }}><input type="number" step="any" className="form-input" value={item.qty_per_kg ?? ''} onChange={(e) => updateItem(index, 'qty_per_kg', e.target.value)} placeholder="Qty/Kg" style={{ width: '70px', fontSize: '12px', padding: '4px' }} data-testid={`item-qtykg-${index}`} /></td>
                              <td style={{ padding: '4px' }}><input type="number" step="any" className="form-input" value={item.qty_per_pouch ?? ''} onChange={(e) => updateItem(index, 'qty_per_pouch', e.target.value)} placeholder="Qty/Pouch" style={{ width: '70px', fontSize: '12px', padding: '4px' }} data-testid={`item-qtypouch-${index}`} /></td>
                              <td style={{ padding: '4px' }}><input type="number" step="any" className="form-input" value={item.moq ?? ''} onChange={(e) => updateItem(index, 'moq', e.target.value)} placeholder="MOQ" style={{ width: '70px', fontSize: '12px', padding: '4px' }} data-testid={`item-moq-${index}`} /></td>
                              <td style={{ padding: '4px' }}><input type="number" step="0.01" className="form-input" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)} required style={{ width: '80px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px' }}><input type="number" className="form-input" value={item.gst_rate} onChange={(e) => updateItem(index, 'gst_rate', parseFloat(e.target.value) || 0)} style={{ width: '50px', fontSize: '12px', padding: '4px' }} /></td>
                              <td style={{ padding: '4px', textAlign: 'right' }} className="tabular-nums">{itemTotal.total_amount.toFixed(2)}</td>
                              <td style={{ padding: '4px' }}>{formData.items.length > 1 && <button type="button" onClick={() => removeItem(index)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex gap-2" style={{ marginTop: '8px' }}>
                    <button type="button" onClick={addItem} className="btn btn-secondary btn-sm">+ Add Item</button>
                    <button type="button" onClick={() => setShowQuickProduct(true)} className="btn btn-ghost btn-sm" style={{ fontSize: '11px' }}><Plus size={12} /> New SKU</button>
                  </div>
                </div>

                {formData.items.length > 0 && formData.items[0].sku && (
                  <div style={{ marginTop: '16px', padding: '16px', backgroundColor: 'var(--bg-surface-alt)', borderRadius: '4px', textAlign: 'right' }}>
                    <div style={{ fontSize: '13px' }}>Subtotal: <span className="tabular-nums">{totals.subtotal.toFixed(2)}</span></div>
                    <div style={{ fontSize: '13px' }}>CGST: <span className="tabular-nums">{totals.cgstTotal.toFixed(2)}</span> | SGST: <span className="tabular-nums">{totals.sgstTotal.toFixed(2)}</span></div>
                    <div style={{ fontSize: '16px', fontWeight: 700, marginTop: '4px' }}>Total: <span className="tabular-nums">{totals.total.toFixed(2)}</span></div>
                  </div>
                )}

                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label className="form-label">Notes / Comments</label>
                  <textarea className="form-textarea" value={formData.notes} maxLength={NOTES_MAX} onChange={(e) => setFormData({ ...formData, notes: e.target.value.slice(0, NOTES_MAX) })} data-testid="notes-input" />
                  <div style={{ textAlign: 'right', fontSize: '11px', color: formData.notes.length >= NOTES_MAX ? '#DC2626' : 'var(--text-tertiary)' }}>{formData.notes.length} / {NOTES_MAX} characters</div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => { setShowModal(false); setEditing(null); }} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-quotation-btn">{editing ? 'Update' : 'Create'} Quotation</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showQuickCustomer && <QuickCreateCustomer onCreated={(c) => { setCustomers(prev => [...prev, c]); selectCustomer(c.id); }} onClose={() => setShowQuickCustomer(false)} />}
      {showQuickProduct && <QuickCreateProduct onCreated={(p) => { setInventory(prev => [...prev, p]); }} onClose={() => setShowQuickProduct(false)} />}
    </div>
  );
};

export default Quotations;
